<script setup lang="ts">
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import PageHeader from '@/components/Globals/PageHeader.vue'
import IcDeleteClose from '@/components/Icons/IcDeleteClose.vue'
import IcEdit from '@/components/Icons/IcEdit.vue'
import { getClassificationTypeNatureBadgeClass, getClassificationTypeNatureLabel, getSensorColor } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useClassificationTypeStore } from '@/store/classificationType'
import EditClassificationTypeModal from '@/views/Alarms/modals/EditClassificationTypeModal.vue'
import RemoveClassificationModal from '@/views/Alarms/modals/RemoveClassificationModal.vue'
import { computed, onMounted, ref } from 'vue'
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { useRouter } from 'vue-router'

const isLoading = ref(true)
const isDeleting = ref(false)
const activeTab = ref<'types' | 'groups'>('types')
const searchQuery = ref('')

const router = useRouter()
const isEditTypeModalOpen = ref(false)
const isRemoveModalOpen = ref(false)
const editingTypeId = ref<number | null>(null)
const editingTypeName = ref('')
const editingTypeNature = ref<string | null>(null)
const removeTarget = ref<{ type: 'type' | 'group'; id: number; name: string } | null>(null)

const store = useClassificationTypeStore()

const types = computed(() => store.listTypes)
const groups = computed(() => store.listGroups)

const filteredTypes = computed(() => {
  const q = searchQuery.value.trim()
  if (!q) return types.value
  return types.value.filter((t) => matchesSearchAccentInsensitive(t.name, q))
})

const filteredGroups = computed(() => {
  const q = searchQuery.value.trim()
  if (!q) return groups.value
  return groups.value.filter((g) => matchesSearchAccentInsensitive(g.name, q))
})

onMounted(async () => {
  isLoading.value = true
  await Promise.all([
    store.fetchTypes({ force: true }),
    store.fetchGroups({ force: true }),
  ])
  isLoading.value = false
})

function handleRefresh() {
  isLoading.value = true
  Promise.all([
    store.fetchTypes({ force: true }),
    store.fetchGroups({ force: true }),
  ]).then(() => {
    isLoading.value = false
  })
}

function openCreateTypeModal() {
  editingTypeId.value = null
  editingTypeName.value = ''
  editingTypeNature.value = null
  isEditTypeModalOpen.value = true
}

function openEditTypeModal(id: number, name: string, nature: string | null) {
  editingTypeId.value = id
  editingTypeName.value = name
  editingTypeNature.value = nature
  isEditTypeModalOpen.value = true
}

function closeEditTypeModal() {
  isEditTypeModalOpen.value = false
  editingTypeId.value = null
}

async function handleTypeSaved(name: string, nature: string | null) {
  try {
    if (editingTypeId.value) {
      await store.updateType(editingTypeId.value, name, nature)
    } else {
      await store.createType(name, nature)
    }
    closeEditTypeModal()
  } catch {
    // Erro ao salvar tipo
  }
}

function openCreateGroup() {
  router.push({ name: 'classification-type-group-new' })
}

function openEditGroup(group: (typeof groups.value)[0]) {
  router.push({ name: 'classification-type-group-edit', params: { id: String(group.id) } })
}

function openSensorDetails(sensor: { id: number }) {
  router.push({ name: 'sensor-detail', params: { id: String(sensor.id) } })
}

function openRemoveModal(type: 'type' | 'group', id: number, name: string) {
  removeTarget.value = { type, id, name }
  isRemoveModalOpen.value = true
}

function closeRemoveModal() {
  isRemoveModalOpen.value = false
  removeTarget.value = null
  isDeleting.value = false
}

async function confirmRemove() {
  if (!removeTarget.value) return
  isDeleting.value = true
  try {
    if (removeTarget.value.type === 'type') {
      await store.deleteType(removeTarget.value.id)
    } else {
      await store.deleteGroup(removeTarget.value.id)
    }
    closeRemoveModal()
  } catch {
    // Erro ao excluir
  } finally {
    isDeleting.value = false
  }
}

</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'dashboard' }"
        title="Tipos de Classificação"
        subtitle="Gerencie os tipos de classificação de alarmes e os grupos que vinculam tipos a sensores ou grupos de sensores.">
        <template #actions>
          <div class="flex items-center gap-2">
            <router-link
              :to="{ name: 'alarms-listing' }"
              class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
              Classificar alarmes
            </router-link>
            <button
              type="button"
              class="flex items-center justify-center border border-stroke p-2 text-slate-700 transition hover:bg-slate-50"
              title="Atualizar"
              @click="handleRefresh">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </template>
      </PageHeader>

      <!-- Tabs -->
      <div class="mb-6 border-b border-stroke">
        <nav class="-mb-px flex gap-1" aria-label="Abas">
          <button
            type="button"
            :class="[
              'whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition',
              activeTab === 'types'
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
            ]"
            @click="activeTab = 'types'">
            Tipos simples
          </button>
          <button
            type="button"
            :class="[
              'whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition',
              activeTab === 'groups'
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
            ]"
            @click="activeTab = 'groups'">
            Grupos de classificação
          </button>
        </nav>
      </div>

      <!-- Tab: Tipos simples -->
      <div v-show="activeTab === 'types'" class="space-y-4">
        <div class="flex items-center justify-between">
          <input
            v-model="searchQuery"
            type="text"
            class="w-48 border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="Buscar por nome" />
          <button
            type="button"
            class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
            @click="openCreateTypeModal">
            Novo tipo
          </button>
        </div>
        <div class="flex w-full flex-col border border-stroke">
          <div
            v-for="type in filteredTypes"
            :key="type.id"
            class="flex w-full items-center justify-between border-b border-stroke bg-white px-4 py-4 last:border-b-0">
            <div class="flex items-center gap-3">
              <button
                type="button"
                class="p-2 hover:opacity-80"
                @click="openEditTypeModal(type.id, type.name, type.nature ?? null)">
                <IcEdit height="20" width="20" />
              </button>
              <div>
                <h3 class="text-lg font-bold text-black-2">{{ type.name }}</h3>
                <div class="mt-0.5 flex items-center gap-2">
                  <span class="text-xs text-slate-500">Tipo simples</span>
                  <span
                    v-if="type.nature"
                    :class="getClassificationTypeNatureBadgeClass(type.nature)"
                    class="w-fit px-2 py-0.5 text-sm font-medium">
                    {{ getClassificationTypeNatureLabel(type.nature) }}
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="p-2 hover:opacity-80"
              @click="openRemoveModal('type', type.id, type.name)">
              <IcDeleteClose width="18" height="18" />
            </button>
          </div>
          <div
            v-if="filteredTypes.length === 0"
            class="border border-dashed border-stroke bg-slate-50 py-12 text-center text-slate-500">
            Nenhum tipo de classificação cadastrado. Clique em "Novo tipo" para criar.
          </div>
        </div>
      </div>

      <!-- Tab: Grupos de classificação -->
      <div v-show="activeTab === 'groups'" class="space-y-4">
        <div class="flex items-center justify-between">
          <input
            v-model="searchQuery"
            type="text"
            class="w-48 border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
            placeholder="Buscar por nome" />
            <button
              type="button"
              class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
              @click="openCreateGroup">
              Novo grupo
            </button>
        </div>
        <div class="flex w-full flex-col border border-stroke">
          <div
            v-for="group in filteredGroups"
            :key="group.id"
            class="flex w-full flex-col border-b border-stroke bg-white last:border-b-0">
            <div class="flex items-center justify-between px-4 py-4">
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  class="p-2 hover:opacity-80"
                  @click="openEditGroup(group)">
                  <IcEdit height="20" width="20" />
                </button>
                <h3 class="text-lg font-bold text-black-2">{{ group.name }}</h3>
              </div>
              <button
                type="button"
                class="p-2 hover:opacity-80"
                @click="openRemoveModal('group', group.id, group.name)">
                <IcDeleteClose width="18" height="18" />
              </button>
            </div>
            <div
              v-if="(group.classification_types ?? []).length > 0 || (group.sensors ?? []).length > 0 || (group.groups ?? []).length > 0"
              class="border-t border-stroke bg-slate-50 px-4 py-3">
              <div v-if="(group.classification_types ?? []).length > 0" class="mb-2 flex flex-wrap items-center gap-2">
                <span class="text-base font-medium text-slate-500">Tipos:</span>
                <span
                  v-for="ct in group.classification_types"
                  :key="ct.id"
                  class="inline-flex items-center border border-primary/30 bg-primary/5 px-2 py-0.5 text-xs font-medium text-primary">
                  {{ ct.name }}
                </span>
              </div>
              <div v-if="(group.sensors ?? []).length > 0" class="mb-2">
                <span class="text-base font-medium text-slate-500">Sensores:</span>
                <ul class="mt-1 list-disc">
                  <button
                    v-for="sensor in group.sensors"
                    :key="'s-' + sensor.id"
                    :class="[
                      getSensorColor(sensor.sensor_type),
                      sensor.has_open_alarm && 'sensor-with-open-alarm',
                    ]"
                    class="m-4 mx-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-white opacity-90 hover:opacity-100"
                    type="button"
                    @click="openSensorDetails(sensor)">
                    {{ sensor.name }}
                  </button>
                </ul>
              </div>
              <div v-if="(group.groups ?? []).length > 0" class="flex flex-wrap items-center gap-2">
                <span class="text-xs font-medium text-slate-500">Grupos:</span>
                <span
                  v-for="grp in group.groups"
                  :key="'g-' + grp.id"
                  class="inline-flex items-center border border-blue-300 bg-blue-50 px-2 py-0.5 text-sm font-semibold text-blue-800">
                  {{ grp.name }}
                </span>
              </div>
            </div>
          </div>
          <div
            v-if="filteredGroups.length === 0"
            class="border border-dashed border-stroke bg-slate-50 py-12 text-center text-slate-500">
            Nenhum grupo de classificação cadastrado. Crie tipos primeiro e depois clique em "Novo grupo".
          </div>
        </div>
      </div>
    </div>

    <EditClassificationTypeModal
      :is-open="isEditTypeModalOpen"
      :initial-name="editingTypeName"
      :initial-nature="editingTypeNature"
      :editing-id="editingTypeId"
      @close="closeEditTypeModal"
      @saved="handleTypeSaved" />

    <RemoveClassificationModal
      :is-open="isRemoveModalOpen"
      :item-name="removeTarget?.name ?? ''"
      :is-deleting="isDeleting"
      @close="closeRemoveModal"
      @confirm="confirmRemove" />
  </DefaultLayout>
</template>
